import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@material-ui/core'
import axios from 'axios'
import useAggregateVendors from './useAggregateVendors'
import ComparisonFilterBar from './offerings/ComparisonFilterBar'
import ComparisonGrid from './offerings/ComparisonGrid'
import FlatOfferingsTable from './offerings/FlatOfferingsTable'
import QuickCalculator from './offerings/QuickCalculator'
import CreateOfferingDialog from './offerings/CreateOfferingDialog'
import MapOfferingDialog from './offerings/MapOfferingDialog'

const COMPARISON_PAGE_SIZE = 20

export default function OfferingsTab () {
  const { suppliers: aggregateVendors } = useAggregateVendors()

  // ── View mode (persisted) ────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem('offerings-view-mode') || 'comparison'
  )

  // ── Comparison filters ───────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    materialCategory: '',
    colorFamily: '',
    vendorId: '',
    search: ''
  })

  // ── Comparison data ──────────────────────────────────────────────────────────
  const [comparisonData, setComparisonData] = useState([])
  const [comparisonTotal, setComparisonTotal] = useState(0)
  const [comparisonPage, setComparisonPage] = useState(0)
  const [comparisonLoading, setComparisonLoading] = useState(false)

  // ── Dialog states ────────────────────────────────────────────────────────────
  const [quickCalcOpen, setQuickCalcOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [mapOffering, setMapOffering] = useState(null)

  // ── Prefill states ───────────────────────────────────────────────────────────
  const [calcPrefillMaterial, setCalcPrefillMaterial] = useState(null)
  const [calcPrefillGradation, setCalcPrefillGradation] = useState(null)
  const [createPrefillMaterial, setCreatePrefillMaterial] = useState(null)
  const [createPrefillGradation, setCreatePrefillGradation] = useState(null)

  // ── Fetch comparison data ────────────────────────────────────────────────────
  const fetchComparison = useCallback(() => {
    setComparisonLoading(true)
    const params = { limit: COMPARISON_PAGE_SIZE, skip: comparisonPage * COMPARISON_PAGE_SIZE }
    if (filters.materialCategory) params.materialCategory = filters.materialCategory
    if (filters.colorFamily) params.colorFamily = filters.colorFamily
    if (filters.vendorId) params.vendorId = filters.vendorId
    if (filters.search) params.search = filters.search
    axios.get('/api/aggregate-dashboard/offerings/comparison', { params })
      .then(res => {
        setComparisonData(res.data.groups || [])
        setComparisonTotal(res.data.total || 0)
      })
      .catch(err => console.error('[OfferingsTab] comparison fetch error:', err))
      .finally(() => setComparisonLoading(false))
  }, [comparisonPage, filters])

  useEffect(() => {
    if (viewMode === 'comparison') {
      fetchComparison()
    }
  }, [fetchComparison, viewMode])

  // ── Persist view mode ────────────────────────────────────────────────────────
  const handleViewModeChange = (mode) => {
    setViewMode(mode)
    localStorage.setItem('offerings-view-mode', mode)
  }

  return (
    <Box style={{ padding: 24 }}>
      <ComparisonFilterBar
        filters={filters}
        onFilterChange={(name, value) => { setFilters(f => ({ ...f, [name]: value })); setComparisonPage(0) }}
        onReset={() => { setFilters({ materialCategory: '', colorFamily: '', vendorId: '', search: '' }); setComparisonPage(0) }}
        vendors={aggregateVendors}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        total={viewMode === 'comparison' ? comparisonTotal : null}
        onOpenQuickCalc={() => setQuickCalcOpen(true)}
        onOpenCreate={() => setCreateOpen(true)}
      />

      {viewMode === 'comparison' ? (
        <ComparisonGrid
          data={comparisonData}
          loading={comparisonLoading}
          total={comparisonTotal}
          page={comparisonPage}
          onPageChange={setComparisonPage}
          onOpenCalc={(mat, grad) => { setCalcPrefillMaterial(mat); setCalcPrefillGradation(grad); setQuickCalcOpen(true) }}
          onOpenCreate={(mat, grad) => { setCreatePrefillMaterial(mat); setCreatePrefillGradation(grad); setCreateOpen(true) }}
        />
      ) : (
        <FlatOfferingsTable
          vendors={aggregateVendors}
          onOpenMap={offering => setMapOffering(offering)}
          onIgnore={async (offering) => {
            await axios.post(`/api/aggregate-dashboard/offerings/${offering._id}/ignore`)
          }}
          onOpenCreate={() => setCreateOpen(true)}
          onOpenQuickCalc={() => setQuickCalcOpen(true)}
        />
      )}

      <QuickCalculator
        open={quickCalcOpen}
        onClose={() => { setQuickCalcOpen(false); setCalcPrefillMaterial(null); setCalcPrefillGradation(null) }}
        prefillMaterial={calcPrefillMaterial}
        prefillGradation={calcPrefillGradation}
      />

      <CreateOfferingDialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setCreatePrefillMaterial(null); setCreatePrefillGradation(null) }}
        onCreated={() => { setCreateOpen(false); fetchComparison() }}
        vendors={aggregateVendors}
        prefillMaterialId={createPrefillMaterial}
        prefillGradationId={createPrefillGradation}
      />

      <MapOfferingDialog
        open={!!mapOffering}
        offering={mapOffering}
        onClose={() => setMapOffering(null)}
        onMapped={() => { setMapOffering(null); fetchComparison() }}
      />
    </Box>
  )
}
